// Drawer_MTB.tsx
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { GridApiPremium } from '@mui/x-data-grid-premium'
import { useBatchUpdateOrgChecklists } from 'api/aws/checklists/useBatchUpdateOrgChecklists'
import { useGetOrgChecklists } from 'api/aws/checklists/useGetOrgChecklists'
import { useOptsByOrgId } from 'api/aws/optimizations/useOptsByOrgId'
import { useOrgMonthlyProgress } from 'api/aws/organizations/useOrgMonthlyProgress'
import { useEntitiesFromSearch } from 'api/search'
import { useEnsureMonthlyTasks } from 'api/aws/tasks/useEnsureMonthlyTasks'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useUpdateOrganization } from 'api/aws/useEntityUpdate'
import TabRouting from 'components/Tabs/TabRouting'
import { useActiveDealMapping } from 'components/MTB/grid/useActiveDealMapping'
import { useMtbDrawerOpen } from 'context/DrawerProvider/useDrawerState'
import { DrawerLayout, ExpandCollapseBar } from 'theme/components'
import type { Organization, OrganizationTableItem, User } from 'types'
import { useDateOperators } from 'utils/useDateOperators'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { buildMonthlyTabsConfig } from './config'
import { getDiff } from './getDiff'
import MtbGridHeader from './MtbGridHeader'
import { useMtbMonthlyTasks } from './useMtbMonthlyTasks'

dayjs.extend(utc)
dayjs.extend(timezone)

// Stable empty arrays to prevent reference changes causing re-renders
const EMPTY_ARRAY: never[] = []

type Props = {
  onRequestClose?: () => void
  isLoading?: boolean
  visibleRowIds?: string[]
  apiRef?: React.RefObject<GridApiPremium>
}

type PendingChecklistUpdate = {
  PK: string
  SK: string
  itemId: string
  newStatus: string
  fieldName: 'status'
  sectionIndex: number
  itemIndex: number
}

const Drawer_MTB: React.FC<Props> = ({ onRequestClose, apiRef: _apiRef }) => {
  const { PK } = useParams<{ PK: string }>()
  const orgId = useMemo(() => PK?.split('ORG_')?.[1] ?? null, [PK])
  // const visibleRowIds: GridRowId[] = useMemo(() => {
  //   if (!apiRef?.current) return []
  //   return gridFilteredSortedRowIdsSelector(apiRef) // returns GridRowId[]
  // }, [apiRef])
  const isMtbDrawerOpen = useMtbDrawerOpen()
  const isMtbDetail = Boolean(PK)
  const { showSnackbar } = useSnackbarContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const getActiveDealMapping = useActiveDealMapping()
  // Data
  const { organizations, deals: allDeals, products, commentsTable } = useEntitiesFromSearch()
  const commentCount = useMemo(() => {
    if (!commentsTable) return null
    return commentsTable?.filter(c => c?.PK === PK && c?.context === 'mtb')?.length ?? null
  }, [commentsTable])

  const deals = useMemo(() => allDeals?.filter((d: OrganizationTableItem) => d?.PK === PK) ?? [], [allDeals, PK])
  const activeProgress = useMemo(() => (deals?.length ? getActiveDealMapping(deals) : []), [deals])
  // Find organization and enrich team_details with full user objects
  const organization = useMemo((): OrganizationTableItem | null => {
    if (!PK) return null
    const rawOrg = organizations?.find((o: OrganizationTableItem) => o?.PK === PK)
    if (!rawOrg) return null

    // Enrich team_details with full user objects from users cache
    const userUUIDs = rawOrg.team_details?.map((u: User) => u?.user_uuid) ?? []
    const enrichedTeamDetails =
      users?.filter((u: User) => userUUIDs.includes(u?.user_uuid)) ?? rawOrg.team_details ?? []

    return {
      ...rawOrg,
      team_details: enrichedTeamDetails,
    } as OrganizationTableItem
  }, [PK, organizations, users])
  // const organization: EnhancedOrg = useMemo(() => {
  //   const org = organizations?.find((o: OrganizationTableItem) => o?.PK === PK) ?? null
  //   const relatedProducts: OrganizationTableItem[] = deals?.filter(d =>
  //     products?.some(p => p?.deal_uuid === d?.deal_uuid)
  //   )
  //   console.log('relatedProducts', relatedProducts)
  //   return org ? { ...org, activeCount: activeProgress[org?.org_uuid], products: relatedProducts ?? [] } : null
  // }, [organizations, activeProgress, PK])
  // console.log('organization', organization)
  const { data: fetchedChecklists, isLoading: isChecklistsLoading } = useGetOrgChecklists(organization)
  const { data: progressData } = useOrgMonthlyProgress(orgId ?? null)
  console.log('progressData', progressData)
  const { data: optimizations, isLoading: isOptimizationsLoading } = useOptsByOrgId(organization?.org_uuid)

  // Use idempotent endpoint - creates tasks if needed, returns existing if already created
  const { data: tasksResponse, isLoading: isTasksLoading } = useEnsureMonthlyTasks(
    orgId,
    organization?.team_details ?? null,
    { enabled: !!organization }
  )
  // Memoize to prevent new [] reference on every render
  const monthlyTasks = useMemo(() => tasksResponse?.tasks ?? [], [tasksResponse?.tasks])
  const isTasksByOrgIdLoading = isTasksLoading

  // Local UI
  const [expanded, setExpanded] = useState(false)
  const [optimizationExpandedId, setOptimizationExpandedId] = useState<string | null>(null)

  // Checklist updates
  const [pendingChecklistUpdates, setPendingChecklistUpdates] = useState<PendingChecklistUpdate[]>([])
  const { mutateAsync: batchUpdateAsync } = useBatchUpdateOrgChecklists()

  // Org updates
  const [pendingOrgUpdates, setPendingOrgUpdates] = useState<Record<string, unknown>>({})
  const { mutate: updateOrganization } = useUpdateOrganization()

  // Monthly tasks helpers
  const detectChanges = useCallback(() => {
    // if you still need a “dirty” flag, keep it; otherwise you can remove this
  }, [])
  const {
    handleMonthlyTaskChange,
    handleUpdateMonthlyTaskStatus,
    setMonthlyTaskChanges,
    updateMonthlyTaskActiveAssignee,
  } = useMtbMonthlyTasks({ orgId, monthlyTasks, detectChanges })

  // Combined for comments tab
  const combinedTasksAndOptimizations = useMemo(() => {
    if (isOptimizationsLoading || isTasksByOrgIdLoading) return EMPTY_ARRAY
    const opts = Array.isArray(optimizations) ? optimizations : EMPTY_ARRAY
    const mts = Array.isArray(monthlyTasks) ? monthlyTasks : EMPTY_ARRAY
    if (opts.length === 0 && mts.length === 0) return EMPTY_ARRAY
    return [...opts, ...mts]
  }, [optimizations, isOptimizationsLoading, monthlyTasks, isTasksByOrgIdLoading])
  const combinedSKs = useMemo(() => combinedTasksAndOptimizations.map(s => s?.SK), [combinedTasksAndOptimizations])

  // Integration fields (for Checklists tab)
  const [integrationFields, setIntegrationFields] = useState<Record<string, string>>({})
  useEffect(() => {
    if (!organization) return
    setIntegrationFields({
      googleAdsId: organization?.googleAdsId || '',
      metaAccountId: organization?.metaAccountId || '',
      metaPixelId: organization?.metaPixelId || '',
      tiktokAccountId: organization?.tiktokAccountId || '',
      tiktokPixelId: organization?.tiktokPixelId || '',
      simplifiOrgId: organization?.simplifiOrgId || '',
      bingAccountId: organization?.bingAccountId || '',
      bingUetId: organization?.bingUetId || '',
      websiteNotes: organization?.websiteNotes || '',
      trackedConversions: organization?.trackedConversions || '',
    })
  }, [organization])

  // Checklist status change → stage a pending update (no local optimistic UI here)
  const handleStatusChange = useCallback((checklist: any, itemId: string, newStatus: string) => {
    if (!itemId || !newStatus) return

    // find indices in the provided checklist (caller has the structure)
    const findIndices = (cl: any, id: string) => {
      for (let s = 0; s < cl.sections.length; s++) {
        const section = cl.sections[s]
        for (let i = 0; i < section.items.length; i++) {
          if (section.items[i].id === id) return { sectionIndex: s, itemIndex: i }
        }
      }
      return null
    }

    const indices = findIndices(checklist, itemId)
    if (!indices) return

    setPendingChecklistUpdates(prev => [
      ...prev.filter(u => u.itemId !== itemId),
      {
        PK: checklist.PK,
        SK: checklist.SK,
        itemId,
        newStatus,
        fieldName: 'status',
        sectionIndex: indices.sectionIndex,
        itemIndex: indices.itemIndex,
      },
    ])
  }, [])

  const handleUpdate = useCallback(async () => {
    if (!organization) return
    if (Object.keys(pendingOrgUpdates).length === 0) return

    const orgIdLocal = organization.org_uuid
    const newOrganization = { ...organization, ...pendingOrgUpdates, queryKey: ['organization', orgIdLocal] }
    const diff = getDiff(organization, newOrganization)
    if (Object.keys(diff).length === 0) return

    await new Promise<void>((resolve, reject) => {
      updateOrganization(
        { orgId: orgIdLocal, orgData: diff },
        {
          onSuccess: () => {
            setPendingOrgUpdates({})
            resolve()
          },
          onError: err => {
            console.error('Error updating organization', err)
            setPendingOrgUpdates({})
            reject(err)
          },
        }
      )
    })
  }, [organization, pendingOrgUpdates, updateOrganization])

  const handleClose = useCallback(() => {
    // Close drawer immediately for responsive UX
    onRequestClose?.()

    // Flush updates in background (don't block close animation)
    if (pendingChecklistUpdates.length > 0) {
      batchUpdateAsync(pendingChecklistUpdates)
        .then(res => {
          if ((res as any)?.status === 200) {
            queryClient.invalidateQueries({ queryKey: ['organization', organization?.org_uuid] })
          }
        })
        .catch(err => {
          console.error('Error updating checklists:', err)
        })
        .finally(() => {
          setPendingChecklistUpdates([])
        })
    }

    // Flush org updates in background
    handleUpdate()
  }, [batchUpdateAsync, handleUpdate, onRequestClose, organization?.org_uuid, pendingChecklistUpdates, queryClient])

  const handleAddTaskComment = useCallback(
    async (selectedTask: any, newNoteObj: any) => {
      if (!selectedTask || !newNoteObj) return

      const updatedComments = [
        { ...newNoteObj, context: selectedTask?.title || selectedTask?.name },
        ...(selectedTask.comments || []),
      ]

      // Build minimal batch payload; delegate write shape to your batchUpdate endpoint
      const payload = [
        {
          PK: selectedTask.PK,
          SK: selectedTask.SK,
          itemId: selectedTask.id,
          fieldName: 'comments',
          newValue: updatedComments,
        },
      ]

      try {
        await batchUpdateAsync(payload as any)
        showSnackbar('Task comment added successfully', 'success')
      } catch (err) {
        console.error('Failed to add task comment:', err)
        showSnackbar('Failed to add task comment', 'error')
      }
    },
    [batchUpdateAsync, showSnackbar]
  )

  // Month key & tabs config
  const { monthKeyFromISO, currentMonthKey } = useDateOperators()
  const userTimezone = useMemo(() => dayjs.tz.guess() || 'America/Detroit', [])
  const monthKey = useMemo(() => currentMonthKey(userTimezone), [currentMonthKey, userTimezone])

  // Note: seoMonthly, amMonthly, dmsMonthly, and assignees are now computed inside buildMonthlyTabsConfig
  const { AM_USERS, DMS_USERS, SEO_USERS, INTEGRATION_USERS } = useMemo<{
    AM_USERS: User[]
    DMS_USERS: User[]
    SEO_USERS: User[]
    INTEGRATION_USERS: User[]
  }>(() => {
    const listIds: string[] = Array.isArray(organization?.team_details)
      ? organization!.team_details?.map(u => u?.user_uuid)
      : []
    const list: User[] = users?.filter(u => listIds?.includes(u?.user_uuid)) ?? []
    const pick = (role: string) => list.filter(m => String(m?.role ?? '').toUpperCase() === role)

    return {
      AM_USERS: pick('AM'),
      DMS_USERS: pick('DMS'),
      SEO_USERS: pick('SEO'),
      INTEGRATION_USERS: pick('MIA'),
    }
  }, [organization?.team_details, users])
  const tabs = useMemo(() => {
    if (isTasksByOrgIdLoading || !organization) return []

    return buildMonthlyTabsConfig({
      organization,
      optimizations,
      monthlyTasks,
      handleMonthlyTaskChange,
      handleUpdateMonthlyTaskStatus,
      updateMonthlyTaskActiveAssignee,
      combinedSKs,
      combinedTasksAndOptimizations,
      handleAddTaskComment,
      deals,
      integrationFields,
      setIntegrationFields,
      handleStatusChange,
      fetchedChecklists,
      isChecklistsLoading,
      context: 'mtb',
      expanded,
      onExpandedIdChange: setOptimizationExpandedId,
      monthKey,
      monthKeyFromISO,
      tz: userTimezone,
    })
  }, [
    isTasksByOrgIdLoading,
    organization,
    optimizations,
    monthlyTasks,
    handleMonthlyTaskChange,
    handleUpdateMonthlyTaskStatus,
    updateMonthlyTaskActiveAssignee,
    combinedSKs,
    combinedTasksAndOptimizations,
    expanded,
    monthKey,
    monthKeyFromISO,
    userTimezone,
  ])

  // Stable references for TabRouting props
  const handleSaveAll = useCallback(() => {}, [])
  const teamDetails = useMemo(() => ({ AM_USERS, DMS_USERS, SEO_USERS, INTEGRATION_USERS }), [
    AM_USERS,
    DMS_USERS,
    SEO_USERS,
    INTEGRATION_USERS,
  ])

  return (
    <DrawerLayout
      open={isMtbDetail}
      onClose={handleClose}
      width="75.5%"
      expanded={expanded}
      onExpandedChange={setExpanded}
      collapsedHeader={
        <ExpandCollapseBar
          entity={organization as Organization}
          isExpanded={expanded}
          progressData={progressData}
          onClose={handleClose}
        />
      }
      expandedHeader={<MtbGridHeader organization={organization} />}
    >
      <TabRouting
        key={PK}
        config={tabs}
        onSaveAll={handleSaveAll}
        team_details={teamDetails}
        count={commentCount}
        isNavigationDisabled={!!optimizationExpandedId}
      />
    </DrawerLayout>
  )
}

export default Drawer_MTB
